import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTrigger,
  makeStyles,
} from "@fluentui/react-components";
import { FilterFilled } from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { QueryFilter } from "../../models/QueryFilter";
import { SearchFilters } from "./SearchFilters";

interface SearchFiltersDialogProps {
  endpoint: string;
  defaultSelectedFilterValues: QueryFilter[];
  onFilter: (newFilterValues: QueryFilter[]) => void;
}

const useStyles = makeStyles({
  dialog: {
    width: "1200px",
    maxWidth: "1200px",
  },
  dialogContent: {
    minHeight: "470px",
  },
  filterButton: {
    borderRight: 0,
    borderRadius: "5px 0 0 5px",
    width: "40px",
    minWidth: "40px",
    height: "32px",
    paddingLeft: 0,
    paddingRight: 0,
  },
});

export const SearchFiltersDialog = (props: SearchFiltersDialogProps) => {
  const classes = useStyles();
  const { endpoint, onFilter, defaultSelectedFilterValues } = props;
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [selectedFilterValues, setSelectedFilterValues] = useState<
    QueryFilter[]
  >(defaultSelectedFilterValues);

  const onFilterClick = () => {
    if (selectedFilterValues.length > 0) {
      searchParams.set("filters", JSON.stringify(selectedFilterValues));
    } else {
      searchParams.delete("filters");
    }

    setSearchParams(searchParams, {
      preventScrollReset: true,
    });

    onFilter(selectedFilterValues);
  };

  const handleClearFilters = () => {
    setSelectedFilterValues([]);
  };

  const handleOpenChange = (_event: unknown, data: { open: boolean }) => {
    setOpen(data.open);

    if (data.open) {
      setSelectedFilterValues(defaultSelectedFilterValues);
    }
  };

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setSelectedFilterValues(defaultSelectedFilterValues);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [defaultSelectedFilterValues, setSelectedFilterValues]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger disableButtonEnhancement>
        <Button
          className={classes.filterButton}
          icon={<FilterFilled fontSize={20} />}
        />
      </DialogTrigger>
      <DialogSurface className={classes.dialog}>
        <DialogBody>
          <DialogContent className={classes.dialogContent}>
            <SearchFilters
              key={endpoint}
              endpoint={endpoint}
              selectedFilterValues={selectedFilterValues}
              onFiltersChange={setSelectedFilterValues}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClearFilters}>{t("clearAllFilters")}</Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary" onClick={onFilterClick}>
                {t("setFilter")}
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
